<template>
  <MultiSelectDefault :name="props.name" />
  <AtomicUiForm ref="customForm" @submit="addCustomPriceRange($event)">
    <p class="heavy margin-bottom-xs margin-top-s">Custom Range</p>
    <div class="grid-x align-top custom-range">
      <div class="cell range-input margin-right-xxs">
        <AtomicUiInputText ref="min" v-model="customRange.minVal" type="number" name="min" label="Min" />
      </div>
      <div class="cell range-text shrink padding-top-xs margin-right-xxs">to</div>
      <div class="cell range-input margin-right-xs">
        <AtomicUiInputText ref="max" v-model="customRange.maxVal" class="cell" type="number" name="max" label="Max" :hide-error-message="true" />
      </div>
      <div class="cell range-button">
        <AtomicUiButton variant="secondary" type="submit"> Go </AtomicUiButton>
      </div>
    </div>
  </AtomicUiForm>
</template>

<script setup>
import MultiSelectDefault from '~/components/FilteringFacets/Criteria/MultiSelectDefault';
import useSortFilter from '~/composables/filteringFacets/useSortFilter';
import useDisplay from '~/composables/filteringFacets/useDisplay';
import { useFilteringFacetsStore } from '~/stores/filteringFacets';
import { GET_ALL_SELECTED_CRITERIA, ADD_CHECKBOX_VALUES, REPLACE_SELECTED_VALUES, REMOVE_CHECKBOX_VALUES_BY_VALUE } from '~/types/actions';

const filteringFacetsStore = useFilteringFacetsStore();
const { screen } = useDisplay();
const { removeCheckBoxValuesByValue, facetByName, updateFacet, addCheckboxValue } = useSortFilter();
const props = defineProps({
  name: {
    type: String,
    default: 'Category',
  },
});

const customRange = ref({
  minVal: '',
  maxVal: '',
});
const customForm = ref(null);

watch(
  () => filteringFacetsStore.dropdownClosed,
  () => {
    customRange.value = {
      minVal: '',
      maxVal: '',
    };
    if (customForm.value) {
      customForm.value.resetForm();
    }
  },
);

const isInvalid = (minVal, maxVal) => {
  const minValNumber = parseInt(minVal, 10);
  const maxValNumber = parseInt(maxVal, 10);
  return !minVal || !maxVal || minValNumber >= maxValNumber || minValNumber < 1 || maxValNumber > 9999;
};

const addCustomPriceRange = (values) => {
  const { min, max } = values;
  const { minVal, maxVal } = customRange.value;

  if (isInvalid(min, max)) {
    const minNumber = parseInt(min, 10);
    const maxNumber = parseInt(max, 10);
    const message = 'Please enter a range from 1 to 9999';
    const minErr = !min || minNumber < 1 || (min && max && minNumber >= maxNumber);
    const maxErr = !max || maxNumber > 9999 || (min && max && minNumber >= maxNumber);
    const errors = {};
    if (minErr) {
      errors.min = message;
    }
    if (maxErr) {
      errors.max = errors.min ? ' ' : message;
    }
    customForm.value.setErrors(errors);
    return;
  }
  if (screen.value.isMobile) {
    const facet = facetByName(props.name);
    const hasCustomPrice = facet.values.some((value) => value.label.indexOf('Custom:') > -1);

    if (hasCustomPrice) {
      removeCheckBoxValuesByValue({
        name: props.name,
        value: facet.selectedValues[0],
      });
    }

    addCheckboxValue({
      name: props.name,
      newValue: {
        label: `Custom: ${minVal} - ${maxVal}`,
        value: `${minVal}|${maxVal}`,
      },
    });

    updateFacet({
      name: props.name,
      selectedValues: [`${minVal}|${maxVal}`],
    });
  } else {
    const facet = filteringFacetsStore.facetByName(props.name);
    const hasCustomPrice = facet.values.some((value) => value.label.indexOf('Custom:') > -1);

    if (hasCustomPrice) {
      filteringFacetsStore[REMOVE_CHECKBOX_VALUES_BY_VALUE]({
        name: props.name,
        value: facet.selectedValues[0],
      });
    }

    filteringFacetsStore[ADD_CHECKBOX_VALUES]({
      name: props.name,
      newValue: {
        label: `Custom: ${minVal} - ${maxVal}`,
        value: `${minVal}|${maxVal}`,
      },
    });

    filteringFacetsStore[REPLACE_SELECTED_VALUES]({
      name: props.name,
      selectedValues: [`${minVal}|${maxVal}`],
    });
    filteringFacetsStore[GET_ALL_SELECTED_CRITERIA]();
  }

  customRange.value = {
    minVal: '',
    maxVal: '',
  };
  customForm.value.resetForm();
};
</script>

<style scoped lang="scss">
:deep(.input-container) {
  margin-top: 0 !important;
  margin-bottom: 0 !important;
}
.custom-range {
  width: rem-calc(264);
  flex-flow: row;
  :deep(.form-error) {
    &.is-visible {
      margin: rem-calc(8 0 0 0);
      white-space: nowrap;
    }
  }
}
.range-text {
  color: $gray-1-color;
}
.range-input {
  width: rem-calc(80);
}
.range-button {
  width: rem-calc(60);
}
:deep(.custom-range) {
  .range-text + .range-input {
    .form-error.is-visible {
      margin-left: rem-calc(-108) !important;
    }
  }
  .range-input {
    .form-error {
      svg {
        display: none;
      }
    }
  }
}
</style>
